/**
 * FunnelPopup
 *
 * Bottom-right slide-up toast for homepage funnel experiences.
 * Supports page_content blocks when configured, falls back to hardcoded layout.
 * Auto-dismisses after 10 seconds.
 */

import styled from '@emotion/styled';
import { useState, useEffect, useRef, useCallback } from 'react';
import type { FunnelOffer, StepPageContent, StepPageBlock, DisplaySettings } from '../types';
import type { BlockCallbacks } from './ComponentRenderers';
import { useOfferImage } from '../use-offer-image';
import { normalizeBlocks, renderBlocks, blocksHaveCta } from './BlockRenderer';

const CLOSE_ANIMATION_MS = 260;

const AUTO_DISMISS_MS = 10_000;

interface PopupContainerProps {
  $closing: boolean;
}

const PopupContainer = styled.div<PopupContainerProps>`
  position: fixed;
  /* Position and size via CSS custom properties (set inline by displaySettings) */
  bottom: var(--popup-bottom, 24px);
  right: var(--popup-right, 24px);
  top: var(--popup-top, auto);
  left: var(--popup-left, auto);
  width: var(--popup-width, 340px);
  transform: var(--popup-transform, none);
  max-width: calc(100vw - 48px);
  max-height: 80vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 1500;
  animation: ${({ $closing }) => ($closing ? 'popupSlideDown' : 'popupSlideUp')} 0.35s ease forwards;

  @keyframes popupSlideUp {
    from { transform: translateY(100%); opacity: 0; }
    to { transform: var(--popup-transform, none); opacity: 1; }
  }

  @keyframes popupSlideDown {
    from { transform: var(--popup-transform, none); opacity: 1; }
    to { transform: translateY(100%); opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PopupImage = styled.img`
  width: 100%;
  max-height: 200px;
  object-fit: contain;
`;

const PopupBody = styled.div`
  padding: 16px;
`;

const BlockContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PopupHeadline = styled.h4`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 6px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
`;

const CurrentPrice = styled.span`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: line-through;
`;

const CTAButton = styled.button`
  width: 100%;
  padding: 10px;
  font-size: 14px;
  font-weight: 700;
  color: white;
  background: #10b981;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover { opacity: 0.9; }
`;

const DeclineLink = styled.button`
  display: block;
  margin: 8px auto 0;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;

  &:hover { color: ${({ theme }) => theme.colors.text.primary}; }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  color: white;
  font-size: 16px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  z-index: 1;

  &:hover { background: rgba(0, 0, 0, 0.6); }
`;

const OfferSelector = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const OfferSelectorButton = styled.button<{ $selected: boolean }>`
  border: 1px solid ${({ $selected }) => ($selected ? '#111827' : '#d1d5db')};
  background: ${({ $selected }) => ($selected ? '#111827' : '#ffffff')};
  color: ${({ $selected }) => ($selected ? '#ffffff' : '#111827')};
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`;

interface FunnelPopupProps {
  offer: FunnelOffer;
  offers?: FunnelOffer[];
  selectedOfferId?: string;
  onSelectOffer?: (offerId: string) => void;
  pageContent?: StepPageContent | StepPageBlock[] | null;
  errorMessage?: string | null;
  onAccept: () => void;
  onDecline: () => void;
  onDismiss: () => void;
  accepting?: boolean;
  displaySettings?: DisplaySettings | null;
}

const POSITION_STYLES: Record<string, { top?: string; bottom?: string; left?: string; right?: string }> = {
  'bottom-right': { bottom: '24px', right: '24px' },
  'bottom-left': { bottom: '24px', left: '24px' },
  'top-right': { top: '24px', right: '24px' },
  'top-left': { top: '24px', left: '24px' },
  'center': { top: '50%', left: '50%' },
};

const SIZE_WIDTHS: Record<string, string> = {
  small: '280px',
  medium: '340px',
  large: '420px',
};

function safePrice(value: number | null | undefined): string {
  return Number.isFinite(value) ? `$${(value as number).toFixed(2)}` : '$0.00';
}

export function FunnelPopup({ offer, offers, selectedOfferId, onSelectOffer, pageContent, errorMessage, onAccept, onDecline, onDismiss, accepting, displaySettings }: FunnelPopupProps) {
  const offerImage = useOfferImage(offer.imageUrl, offer.productId);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [isClosing, setIsClosing] = useState(false);
  const blocks = normalizeBlocks(pageContent);
  const hasBlocks = blocks.length > 0;
  const hasCtaBlock = hasBlocks && blocksHaveCta(blocks);
  const multiOffers = Array.isArray(offers) && offers.length > 1 ? offers : null;

  const startClose = useCallback(
    (action: () => void) => {
      clearTimeout(autoTimerRef.current);
      setIsClosing(true);
      closeTimerRef.current = setTimeout(() => {
        setIsClosing(false);
        action();
      }, CLOSE_ANIMATION_MS);
    },
    []
  );

  useEffect(() => {
    autoTimerRef.current = setTimeout(() => startClose(onDismiss), AUTO_DISMISS_MS);
    return () => {
      clearTimeout(autoTimerRef.current);
      clearTimeout(closeTimerRef.current);
    };
  }, [onDismiss, startClose]);

  const callbacks: BlockCallbacks = {
    onAccept: () => startClose(onAccept),
    onDecline: () => startClose(onDecline),
    accepting,
  };

  // Build CSS custom properties from displaySettings (fallback to defaults via CSS vars)
  const posStyles = POSITION_STYLES[displaySettings?.position ?? 'bottom-right'] ?? POSITION_STYLES['bottom-right'];
  const widthValue = displaySettings?.size === 'custom' && displaySettings.customWidth
    ? `${displaySettings.customWidth}px`
    : SIZE_WIDTHS[displaySettings?.size ?? ''] ?? undefined; // undefined = use CSS var default (340px)
  const isCenter = displaySettings?.position === 'center';

  const containerStyle: Record<string, string> = {};
  if (posStyles.top) containerStyle['--popup-top'] = posStyles.top;
  if (posStyles.bottom) containerStyle['--popup-bottom'] = posStyles.bottom;
  if (posStyles.left) containerStyle['--popup-left'] = posStyles.left;
  if (posStyles.right) containerStyle['--popup-right'] = posStyles.right;
  if (!posStyles.bottom) containerStyle['--popup-bottom'] = 'auto';
  if (!posStyles.right) containerStyle['--popup-right'] = 'auto';
  if (widthValue) containerStyle['--popup-width'] = widthValue;
  if (isCenter) containerStyle['--popup-transform'] = 'translate(-50%, -50%)';

  return (
    <PopupContainer $closing={isClosing} style={containerStyle as React.CSSProperties}>
      <CloseButton
        onClick={() => startClose(onDismiss)}
        aria-label="Close"
      >&times;</CloseButton>

      {hasBlocks ? (
        <>
          {multiOffers && onSelectOffer && (
            <BlockContent>
              <OfferSelector aria-label="Offer selector">
                {multiOffers.map((item, index) => {
                  const isSelected = (selectedOfferId ?? offer.id) === item.id || (!selectedOfferId && index === 0);
                  return (
                    <OfferSelectorButton
                      key={item.id}
                      type="button"
                      $selected={isSelected}
                      aria-pressed={isSelected}
                      onClick={() => onSelectOffer(item.id)}
                    >
                      {item.name || item.headline || item.id}
                    </OfferSelectorButton>
                  );
                })}
              </OfferSelector>
            </BlockContent>
          )}
          <BlockContent>{renderBlocks(blocks, offer, callbacks)}</BlockContent>
          {!hasCtaBlock && (
            <PopupBody>
              {errorMessage && <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#b42318' }}>{errorMessage}</p>}
              <CTAButton onClick={() => startClose(onAccept)} disabled={accepting}>
                {accepting ? 'Adding...' : offer.ctaText}
              </CTAButton>
              <DeclineLink onClick={() => startClose(onDecline)}>{offer.declineText}</DeclineLink>
            </PopupBody>
          )}
        </>
      ) : (
        <>
          {offerImage && <PopupImage src={offerImage} alt={offer.headline} />}
          <PopupBody>
            {multiOffers && onSelectOffer && (
              <OfferSelector aria-label="Offer selector">
                {multiOffers.map((item, index) => {
                  const isSelected = (selectedOfferId ?? offer.id) === item.id || (!selectedOfferId && index === 0);
                  return (
                    <OfferSelectorButton
                      key={item.id}
                      type="button"
                      $selected={isSelected}
                      aria-pressed={isSelected}
                      onClick={() => onSelectOffer(item.id)}
                    >
                      {item.name || item.headline || item.id}
                    </OfferSelectorButton>
                  );
                })}
              </OfferSelector>
            )}
            <PopupHeadline>{offer.headline}</PopupHeadline>
            {errorMessage && <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#b42318' }}>{errorMessage}</p>}
            <PriceRow>
              <CurrentPrice>{safePrice(offer.pricingValue)}</CurrentPrice>
              <OriginalPrice>{safePrice(offer.originalPrice)}</OriginalPrice>
            </PriceRow>
            <CTAButton onClick={() => startClose(onAccept)} disabled={accepting}>
              {accepting ? 'Adding...' : offer.ctaText}
            </CTAButton>
            <DeclineLink onClick={() => startClose(onDecline)}>{offer.declineText}</DeclineLink>
          </PopupBody>
        </>
      )}
    </PopupContainer>
  );
}
